// AI Dispatch Suggestions Service
// Uses OpenAI API for intelligent dispatch recommendations
// Falls back to rule-based suggestions when API not configured

const AI_CONFIG_KEY = 'hmgc_ai_config';

export const getAIConfig = () => {
  try {
    const config = localStorage.getItem(AI_CONFIG_KEY);
    return config ? JSON.parse(config) : null;
  } catch (e) {
    console.error('Error reading AI config:', e);
    return null;
  }
};

export const saveAIConfig = (config) => {
  try {
    localStorage.setItem(AI_CONFIG_KEY, JSON.stringify(config));
    return true;
  } catch (e) {
    console.error('Error saving AI config:', e);
    return false;
  }
};

export const isAIConfigured = () => {
  const config = getAIConfig();
  return config && config.apiKey;
};

// Calculate distance score between two addresses (simplified - uses string matching)
// In production, would use geocoding API
const calculateLocationScore = (address1, address2) => {
  if (!address1 || !address2) return 0;
  
  const normalize = (str) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
  const a1 = normalize(address1);
  const a2 = normalize(address2);
  
  // Check for city/state matches
  const cities1 = address1.toLowerCase().split(',').map(s => s.trim());
  const cities2 = address2.toLowerCase().split(',').map(s => s.trim());
  
  let score = 0;
  cities1.forEach(c1 => {
    cities2.forEach(c2 => {
      if (c1 === c2) score += 30;
      else if (c1.includes(c2) || c2.includes(c1)) score += 15;
    });
  });
  
  return Math.min(score, 100);
};

// Calculate driver availability score
const calculateAvailabilityScore = (driver) => {
  if (!driver) return 0;
  
  let score = 0;
  
  // Check status
  if (driver.status === 'available') score += 50;
  else if (driver.status === 'on_duty') score += 30;
  else if (driver.status === 'on_break') score += 10;
  else score += 0; // off_duty, unavailable
  
  // Check if driver is marked as available
  if (driver.is_available) score += 30;
  
  return score;
};

// Calculate truck suitability score
const calculateTruckScore = (truck, load) => {
  if (!truck) return 0;
  
  let score = 0;
  
  // Check truck status
  if (truck.status === 'available' || truck.is_active) score += 40;
  else if (truck.status === 'in_use') score += 10;
  else score += 0; // maintenance, out_of_service
  
  // Check capacity vs load weight
  if (truck.capacity_tons && load.weight_kg) {
    const capacityKg = truck.capacity_tons * 1000;
    if (capacityKg >= load.weight_kg) {
      score += 30;
      // Bonus for not over-using capacity
      const utilizationRatio = load.weight_kg / capacityKg;
      if (utilizationRatio > 0.5 && utilizationRatio < 0.9) {
        score += 20; // Optimal utilization
      }
    }
  } else {
    score += 15; // No capacity data, neutral score
  }
  
  return score;
};

// Calculate workload balance score (prefer drivers with fewer active loads)
const calculateWorkloadScore = (driver, allLoads) => {
  if (!driver || !allLoads) return 50;
  
  const activeStatuses = ['assigned', 'en_route', 'at_pickup', 'loading', 'in_transit', 'at_delivery', 'unloading'];
  const driverLoads = allLoads.filter(l => 
    l.driver_id === driver.id && activeStatuses.includes(l.status)
  );
  
  // Fewer loads = higher score
  if (driverLoads.length === 0) return 100;
  if (driverLoads.length === 1) return 70;
  if (driverLoads.length === 2) return 40;
  return 10;
};

// Rule-based dispatch suggestions (no AI required)
export const getRuleBasedSuggestions = (load, drivers, trucks, allLoads) => {
  const suggestions = [];
  
  // Get pickup address for location matching
  const pickupStop = load.load_stops?.find(s => s.stop_type === 'pickup');
  const pickupAddress = pickupStop?.address || '';
  
  // Score each driver
  const driverScores = drivers.map(driver => {
    const availabilityScore = calculateAvailabilityScore(driver);
    const workloadScore = calculateWorkloadScore(driver, allLoads);
    const locationScore = calculateLocationScore(driver.current_location || '', pickupAddress);
    
    const totalScore = (availabilityScore * 0.4) + (workloadScore * 0.35) + (locationScore * 0.25);
    
    return {
      driver,
      score: Math.round(totalScore),
      reasons: [
        availabilityScore > 50 ? 'Available for assignment' : 'Limited availability',
        workloadScore > 50 ? 'Low current workload' : 'Has active loads',
        locationScore > 30 ? 'Near pickup location' : null
      ].filter(Boolean)
    };
  }).sort((a, b) => b.score - a.score);
  
  // Score each truck
  const truckScores = trucks.map(truck => {
    const suitabilityScore = calculateTruckScore(truck, load);
    
    return {
      truck,
      score: Math.round(suitabilityScore),
      reasons: [
        (truck.status === 'available' || truck.is_active) ? 'Available' : 'In use',
        truck.capacity_tons ? `Capacity: ${truck.capacity_tons}t` : null
      ].filter(Boolean)
    };
  }).sort((a, b) => b.score - a.score);
  
  // Generate top suggestions
  const topDrivers = driverScores.slice(0, 3);
  const topTrucks = truckScores.slice(0, 3);
  
  // Create combined suggestions
  topDrivers.forEach((driverSuggestion, i) => {
    const truckSuggestion = topTrucks[i] || topTrucks[0];
    if (driverSuggestion && truckSuggestion) {
      suggestions.push({
        rank: i + 1,
        driver: driverSuggestion.driver,
        driverScore: driverSuggestion.score,
        driverReasons: driverSuggestion.reasons,
        truck: truckSuggestion.truck,
        truckScore: truckSuggestion.score,
        truckReasons: truckSuggestion.reasons,
        overallScore: Math.round((driverSuggestion.score + truckSuggestion.score) / 2),
        confidence: driverSuggestion.score > 70 && truckSuggestion.score > 50 ? 'high' : 
                   driverSuggestion.score > 40 ? 'medium' : 'low'
      });
    }
  });
  
  return {
    suggestions,
    method: 'rule-based',
    loadInfo: {
      loadNumber: load.load_number,
      customer: load.customer?.company_name,
      commodity: load.commodity,
      weight: load.weight_kg
    }
  };
};

// AI-powered dispatch suggestions using OpenAI
export const getAISuggestions = async (load, drivers, trucks, allLoads) => {
  const config = getAIConfig();
  
  if (!isAIConfigured()) {
    // Fall back to rule-based suggestions
    const ruleBased = getRuleBasedSuggestions(load, drivers, trucks, allLoads);
    return {
      ...ruleBased,
      aiNote: 'AI not configured - using rule-based suggestions. Add OpenAI API key in Settings for smarter recommendations.'
    };
  }
  
  try {
    const prompt = buildDispatchPrompt(load, drivers, trucks, allLoads);
    
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${config.apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: config.model || 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: `You are an expert trucking dispatch assistant. Analyze the load details, available drivers, and trucks to recommend the best assignment. Consider driver availability, workload balance, truck capacity, and efficiency. Respond with JSON only.`
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.3,
        max_tokens: 1000
      })
    });
    
    if (!response.ok) {
      throw new Error('OpenAI API request failed');
    }
    
    const result = await response.json();
    const aiResponse = result.choices[0]?.message?.content;
    
    // Parse AI response
    const parsed = parseAIResponse(aiResponse, drivers, trucks);
    
    return {
      suggestions: parsed.suggestions,
      method: 'ai-powered',
      aiReasoning: parsed.reasoning,
      loadInfo: {
        loadNumber: load.load_number,
        customer: load.customer?.company_name,
        commodity: load.commodity,
        weight: load.weight_kg
      }
    };
  } catch (error) {
    console.error('AI dispatch error:', error);
    // Fall back to rule-based
    const ruleBased = getRuleBasedSuggestions(load, drivers, trucks, allLoads);
    return {
      ...ruleBased,
      aiError: error.message,
      aiNote: 'AI request failed - using rule-based suggestions as fallback.'
    };
  }
};

// Build prompt for AI dispatch
const buildDispatchPrompt = (load, drivers, trucks, allLoads) => {
  const pickupStops = load.load_stops?.filter(s => s.stop_type === 'pickup') || [];
  const deliveryStops = load.load_stops?.filter(s => s.stop_type === 'delivery') || [];
  
  const activeStatuses = ['assigned', 'en_route', 'at_pickup', 'loading', 'in_transit', 'at_delivery', 'unloading'];
  
  const driversInfo = drivers.map(d => ({
    id: d.id,
    name: d.name || `Driver ${d.id}`,
    status: d.status || (d.is_available ? 'available' : 'unavailable'),
    activeLoads: allLoads.filter(l => l.driver_id === d.id && activeStatuses.includes(l.status)).length,
    phone: d.phone
  }));
  
  const trucksInfo = trucks.map(t => ({
    id: t.id,
    number: t.truck_number,
    status: t.status || (t.is_active ? 'available' : 'unavailable'),
    capacity: t.capacity_tons || t.max_weight_kg / 1000
  }));
  
  return JSON.stringify({
    load: {
      id: load.id,
      loadNumber: load.load_number,
      customer: load.customer?.company_name,
      commodity: load.commodity,
      weightKg: load.weight_kg,
      pickups: pickupStops.map(s => s.address),
      deliveries: deliveryStops.map(s => s.address)
    },
    availableDrivers: driversInfo,
    availableTrucks: trucksInfo,
    instruction: 'Recommend top 3 driver-truck combinations. Return JSON with format: { "suggestions": [{ "driverId": "...", "truckId": "...", "score": 0-100, "reasoning": "..." }], "overallReasoning": "..." }'
  }, null, 2);
};

// Parse AI response into structured suggestions
const parseAIResponse = (aiResponse, drivers, trucks) => {
  try {
    // Try to extract JSON from response
    const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('No JSON found in response');
    }
    
    const parsed = JSON.parse(jsonMatch[0]);
    
    const suggestions = (parsed.suggestions || []).map((s, i) => {
      const driver = drivers.find(d => d.id === s.driverId || d.id === parseInt(s.driverId));
      const truck = trucks.find(t => t.id === s.truckId || t.id === parseInt(s.truckId));
      
      return {
        rank: i + 1,
        driver,
        driverScore: s.score || 80,
        driverReasons: [s.reasoning || 'AI recommended'],
        truck,
        truckScore: s.score || 80,
        truckReasons: ['AI recommended'],
        overallScore: s.score || 80,
        confidence: s.score > 80 ? 'high' : s.score > 60 ? 'medium' : 'low',
        aiReasoning: s.reasoning
      };
    }).filter(s => s.driver && s.truck);
    
    return {
      suggestions,
      reasoning: parsed.overallReasoning || 'AI analysis complete'
    };
  } catch (e) {
    console.error('Error parsing AI response:', e);
    return {
      suggestions: [],
      reasoning: 'Could not parse AI response'
    };
  }
};

// Get dispatch suggestions (auto-selects AI or rule-based)
export const getDispatchSuggestions = async (load, drivers, trucks, allLoads) => {
  if (isAIConfigured()) {
    return await getAISuggestions(load, drivers, trucks, allLoads);
  } else {
    return getRuleBasedSuggestions(load, drivers, trucks, allLoads);
  }
};

// Analyze fleet efficiency
export const analyzeFleetEfficiency = (loads, drivers, trucks) => {
  const activeStatuses = ['assigned', 'en_route', 'at_pickup', 'loading', 'in_transit', 'at_delivery', 'unloading'];
  const activeLoads = loads.filter(l => activeStatuses.includes(l.status));
  const completedLoads = loads.filter(l => l.status === 'complete');
  const pendingLoads = loads.filter(l => l.status === 'pending');
  
  const availableDrivers = drivers.filter(d => d.is_available || d.status === 'available');
  const availableTrucks = trucks.filter(t => t.is_active || t.status === 'available');
  
  const driverUtilization = drivers.length > 0 
    ? Math.round((drivers.length - availableDrivers.length) / drivers.length * 100)
    : 0;
    
  const truckUtilization = trucks.length > 0
    ? Math.round((trucks.length - availableTrucks.length) / trucks.length * 100)
    : 0;
  
  return {
    summary: {
      totalLoads: loads.length,
      activeLoads: activeLoads.length,
      completedLoads: completedLoads.length,
      pendingLoads: pendingLoads.length,
      totalDrivers: drivers.length,
      availableDrivers: availableDrivers.length,
      totalTrucks: trucks.length,
      availableTrucks: availableTrucks.length
    },
    utilization: {
      driverUtilization,
      truckUtilization,
      overallEfficiency: Math.round((driverUtilization + truckUtilization) / 2)
    },
    recommendations: generateEfficiencyRecommendations(pendingLoads, availableDrivers, availableTrucks)
  };
};

// Generate efficiency recommendations
const generateEfficiencyRecommendations = (pendingLoads, availableDrivers, availableTrucks) => {
  const recommendations = [];
  
  if (pendingLoads.length > 0 && availableDrivers.length > 0) {
    recommendations.push({
      type: 'action',
      priority: 'high',
      message: `${pendingLoads.length} pending load(s) can be assigned to ${availableDrivers.length} available driver(s)`
    });
  }
  
  if (availableDrivers.length === 0 && pendingLoads.length > 0) {
    recommendations.push({
      type: 'warning',
      priority: 'high',
      message: 'No drivers available - pending loads cannot be assigned'
    });
  }
  
  if (availableTrucks.length === 0 && pendingLoads.length > 0) {
    recommendations.push({
      type: 'warning',
      priority: 'high',
      message: 'No trucks available - consider maintenance schedule review'
    });
  }
  
  if (availableDrivers.length > pendingLoads.length && pendingLoads.length === 0) {
    recommendations.push({
      type: 'info',
      priority: 'low',
      message: 'Fleet capacity available - ready for new loads'
    });
  }
  
  return recommendations;
};

export default {
  getAIConfig,
  saveAIConfig,
  isAIConfigured,
  getRuleBasedSuggestions,
  getAISuggestions,
  getDispatchSuggestions,
  analyzeFleetEfficiency
};
